import cryptoJs from "crypto-js";
import { bytesToHex, decryptData, encryptKeyFromUtf8 } from "./cryptoHelpers";
import { getReqResDek, getSecurityLevel } from "../services/examDetailsService";
import { SECURITY_LEVEL } from "../config/appConstants";

const REQUEST_ENCRYPTION = process.env.REACT_APP_REQUEST_ENCRYPTION === "true"; // or from config

export const handleEncodeJson = async ({
    encodeJSON,
    wrapKey = false
}) => {
    if (!REQUEST_ENCRYPTION) return encodeJSON; // return as-is if encryption is disabled
    try {
        const data = JSON.stringify(encodeJSON);
        const wordArray = cryptoJs.enc.Utf8.parse(data);

        let key = cryptoJs.lib.WordArray.random(32);
        const iv = cryptoJs.lib.WordArray.random(16);

        const encrypted = cryptoJs.AES.encrypt(wordArray, key, {
            iv: iv,
            mode: cryptoJs.mode.CBC,
            padding: cryptoJs.pad.Pkcs7
        })

        key = key.toString(cryptoJs.enc.Hex);
        if (wrapKey) {
            // null when fields are missing or securityLevel is NONE — key sent unwrapped.
            const [reqResDekBytes, level] = await Promise.all([
                getReqResDek(),
                getSecurityLevel(),
            ]);
            if (reqResDekBytes && level && level !== SECURITY_LEVEL.NONE) {
                key = await encryptKeyFromUtf8(reqResDekBytes, key);
            }
        }

        return ({
            encryptedData: encrypted.ciphertext.toString(cryptoJs.enc.Hex),
            key,
            iv: iv.toString(cryptoJs.enc.Hex),
        });
    } catch (error) {
        console.log('There is an error while encoding body', error);
    }
}

export const handleDecodeJSON = async ({ decodeJSON, unwrapKey = false }) => {
    if (!REQUEST_ENCRYPTION) {
        return decodeJSON; // return as-is if encryption is disabled
    }
    try {
        if (!decodeJSON || typeof decodeJSON !== "object") {
            throw new Error("Encrypted response is missing or has an invalid shape");
        }

        let { key, iv, encryptedData } = decodeJSON;
        if (unwrapKey) {
            const [reqResDekBytes, level] = await Promise.all([
                getReqResDek(),
                getSecurityLevel(),
            ]);
            if (reqResDekBytes && level && level !== SECURITY_LEVEL.NONE) {
                const decryptedKeyBytes = await decryptData(
                    bytesToHex(reqResDekBytes),
                    key
                );
                key = new TextDecoder("utf-8").decode(decryptedKeyBytes);
            }
        }

        if (!key || !iv || !encryptedData) {
            throw new Error("Encrypted response is missing key, iv, or encryptedData");
        }

        const decrypted = cryptoJs.AES.decrypt(
            {
                ciphertext: cryptoJs.enc.Hex.parse(encryptedData),
            },
            cryptoJs.enc.Hex.parse(key),
            {
                iv: cryptoJs.enc.Hex.parse(iv),
                mode: cryptoJs.mode.CBC,
                padding: cryptoJs.pad.Pkcs7,
            }
        );
        const decryptedText = decrypted.toString(cryptoJs.enc.Utf8);

        if (!decryptedText) {
            throw new Error("Decryption produced an empty response");
        }
        return JSON.parse(decryptedText);
    } catch (error) {
        console.error("[decode] failed:", error);
    }
};